import React, { useState, useEffect, useRef } from 'react';
import {
  DocumentDuplicateIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ArrowRightIcon,
  ArrowLeftIcon,
  DocumentTextIcon,
  DocumentMagnifyingGlassIcon,
  ScaleIcon,
  PencilSquareIcon,
  ChatBubbleLeftRightIcon,
  ArrowUpTrayIcon,
  ArrowDownTrayIcon,
  XMarkIcon,
  ClipboardDocumentListIcon,
  PrinterIcon,
} from '@heroicons/react/24/outline';
import apiClient, { exportAPI } from '../services/api';
import { useTranslation } from 'react-i18next';
import { ExportDropdownMenu } from '../components/ui/ExportDropdownMenu';
import { LoadingOverlay } from '../components/ui/LoadingOverlay';
import ReactMarkdown from 'react-markdown';
import { marked } from 'marked';

// Types
interface ContractType {
  id: string;
  name: string;
  description: string;
}

interface Priority {
  id: string;
  name: string;
  description: string;
}

// Workflow types
type WorkflowType = null | 'analyze' | 'strategy' | 'redline' | 'talking-points';

// Step definitions for each workflow
const ANALYZE_STEPS = ['uploadContract', 'analysis'];
const STRATEGY_STEPS = ['uploadContract', 'priorities', 'strategy'];
const REDLINE_STEPS = ['uploadContract', 'priorities', 'redline'];
const TALKING_POINTS_STEPS = ['uploadContract', 'priorities', 'talkingPoints'];

export const ContractNegotiation: React.FC = () => {
  const { t, i18n } = useTranslation();

  // Workflow selection
  const [selectedWorkflow, setSelectedWorkflow] = useState<WorkflowType>(null);
  const [currentStep, setCurrentStep] = useState(0);

  // Contract upload state
  const [contractFile, setContractFile] = useState<File | null>(null);
  const [contractText, setContractText] = useState('');
  const [contractTypes, setContractTypes] = useState<ContractType[]>([]);
  const [selectedContractType, setSelectedContractType] = useState('');
  const [industry, setIndustry] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Priority selection
  const [priorities, setPriorities] = useState<Priority[]>([]);
  const [selectedPriorities, setSelectedPriorities] = useState<string[]>([]);
  const [yourPosition, setYourPosition] = useState('');
  const [specificConcerns, setSpecificConcerns] = useState('');
  const [negotiationContext, setNegotiationContext] = useState('');

  // Results state
  const [analysisResult, setAnalysisResult] = useState<string | null>(null);
  const [strategyResult, setStrategyResult] = useState<string | null>(null);
  const [redlineResult, setRedlineResult] = useState<string | null>(null);
  const [talkingPointsResult, setTalkingPointsResult] = useState<string | null>(null);

  // Loading states
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load initial data
  useEffect(() => {
    loadContractTypes();
    loadPriorities();
  }, []);

  const loadContractTypes = async () => {
    try {
      const response = await apiClient.get('/contract-negotiation/contract-types');
      setContractTypes(response.data.contract_types);
    } catch (err) {
      console.error('Failed to load contract types:', err);
    }
  };

  const loadPriorities = async () => {
    try {
      const response = await apiClient.get('/contract-negotiation/negotiation-priorities');
      setPriorities(response.data.priorities);
    } catch (err) {
      console.error('Failed to load priorities:', err);
    }
  };

  // File handling
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const validTypes = ['application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'application/msword', 'text/plain'];
      if (validTypes.includes(file.type) || file.name.endsWith('.pdf') || file.name.endsWith('.docx') || file.name.endsWith('.doc') || file.name.endsWith('.txt')) {
        setContractFile(file);
        setContractText('');
      } else {
        setError(t('contractNegotiation:errors.invalidFileType', 'Please upload a PDF, Word, or TXT document'));
      }
    }
  };

  const clearFile = () => {
    setContractFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Priority handling
  const togglePriority = (priorityId: string) => {
    setSelectedPriorities(prev => {
      if (prev.includes(priorityId)) {
        return prev.filter(p => p !== priorityId);
      }
      return [...prev, priorityId];
    });
  };

  // API calls
  const analyzeContract = async () => {
    if (!contractFile && !contractText.trim()) return;

    setIsLoading(true);
    setError(null);

    try {
      const formData = new FormData();

      if (contractFile) {
        formData.append('file', contractFile);
      } else {
        formData.append('contract_text', contractText);
      }

      if (selectedContractType) formData.append('contract_type', selectedContractType);
      if (industry) formData.append('industry', industry);
      formData.append('language', i18n.language);

      const response = await apiClient.post('/contract-negotiation/analyze', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setAnalysisResult(response.data.analysis);
    } catch (err) {
      setError(t('contractNegotiation:errors.analyzeFailed', 'Failed to analyze contract'));
    } finally {
      setIsLoading(false);
    }
  };

  const generateStrategy = async () => {
    if (!contractFile && !contractText.trim()) return;

    setIsLoading(true);
    setError(null);

    try {
      const formData = new FormData();

      if (contractFile) {
        formData.append('file', contractFile);
      } else {
        formData.append('contract_text', contractText);
      }

      if (selectedContractType) formData.append('contract_type', selectedContractType);
      formData.append('priorities', JSON.stringify(selectedPriorities));
      if (yourPosition) formData.append('your_position', yourPosition);
      if (specificConcerns) formData.append('specific_concerns', specificConcerns);
      formData.append('language', i18n.language);

      const response = await apiClient.post('/contract-negotiation/strategy', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setStrategyResult(response.data.strategy);
    } catch (err) {
      setError(t('contractNegotiation:errors.strategyFailed', 'Failed to generate strategy'));
    } finally {
      setIsLoading(false);
    }
  };

  const generateRedline = async () => {
    if (!contractFile && !contractText.trim()) return;

    setIsLoading(true);
    setError(null);

    try {
      const formData = new FormData();

      if (contractFile) {
        formData.append('file', contractFile);
      } else {
        formData.append('contract_text', contractText);
      }

      if (selectedContractType) formData.append('contract_type', selectedContractType);
      formData.append('priorities', JSON.stringify(selectedPriorities));
      if (yourPosition) formData.append('your_position', yourPosition);
      if (specificConcerns) formData.append('specific_changes', specificConcerns);
      formData.append('language', i18n.language);

      const response = await apiClient.post('/contract-negotiation/redline', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setRedlineResult(response.data.redline);
    } catch (err) {
      setError(t('contractNegotiation:errors.redlineFailed', 'Failed to generate redline'));
    } finally {
      setIsLoading(false);
    }
  };

  const generateTalkingPoints = async () => {
    if (!contractFile && !contractText.trim()) return;

    setIsLoading(true);
    setError(null);

    try {
      const formData = new FormData();

      if (contractFile) {
        formData.append('file', contractFile);
      } else {
        formData.append('contract_text', contractText);
      }

      if (selectedContractType) formData.append('contract_type', selectedContractType);
      formData.append('priorities', JSON.stringify(selectedPriorities));
      if (yourPosition) formData.append('your_position', yourPosition);
      if (negotiationContext) formData.append('negotiation_context', negotiationContext);
      formData.append('language', i18n.language);

      const response = await apiClient.post('/contract-negotiation/talking-points', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setTalkingPointsResult(response.data.talking_points);
    } catch (err) {
      setError(t('contractNegotiation:errors.talkingPointsFailed', 'Failed to generate talking points'));
    } finally {
      setIsLoading(false);
    }
  };

  // Export functions
  const handleExport = async (format: 'word' | 'pdf' | 'text') => {
    await exportResult(format);
  };

  const getCurrentResult = () => {
    switch (selectedWorkflow) {
      case 'analyze': return analysisResult;
      case 'strategy': return strategyResult;
      case 'redline': return redlineResult;
      case 'talking-points': return talkingPointsResult;
      default: return null;
    }
  };

  const getResultTitle = () => {
    switch (selectedWorkflow) {
      case 'analyze': return t('contractNegotiation:results.analysisTitle', 'Contract Analysis');
      case 'strategy': return t('contractNegotiation:results.strategyTitle', 'Negotiation Strategy');
      case 'redline': return t('contractNegotiation:results.redlineTitle', 'Redlined Contract');
      case 'talking-points': return t('contractNegotiation:results.talkingPointsTitle', 'Talking Points');
      default: return 'Contract Negotiation';
    }
  };

  const printResult = () => {
    const result = getCurrentResult();
    if (!result) return;

    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const htmlContent = marked(result, { async: false }) as string;

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>${getResultTitle()}</title>
          <style>
            body {
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
              line-height: 1.6;
              color: #1f2937;
              max-width: 800px;
              margin: 0 auto;
              padding: 40px 20px;
            }
            h1 { font-size: 24px; color: #1e40af; margin-bottom: 8px; }
            h2 { font-size: 18px; color: #1f2937; border-bottom: 1px solid #e5e7eb; padding-bottom: 8px; margin-top: 24px; }
            h3 { font-size: 16px; color: #374151; margin-top: 16px; }
            h4 { font-size: 14px; font-weight: 600; margin-top: 12px; }
            p { margin: 12px 0; }
            ul, ol { margin-left: 24px; margin-bottom: 12px; }
            li { margin: 4px 0; line-height: 1.6; }
            strong { color: #111827; font-weight: 600; }
            em { font-style: italic; }
            del { background-color: #fecaca; color: #991b1b; text-decoration: line-through; }
            code { background-color: #f3f4f6; padding: 2px 6px; border-radius: 3px; font-family: 'Courier New', monospace; }
            pre { background-color: #1f2937; color: #f3f4f6; padding: 12px; border-radius: 6px; overflow-x: auto; }
            blockquote { border-left: 4px solid #60a5fa; padding-left: 16px; font-style: italic; margin: 12px 0; color: #374151; }
            .header { margin-bottom: 24px; padding-bottom: 16px; border-bottom: 2px solid #1e40af; }
            .disclaimer { margin-top: 32px; padding: 16px; background: #fef3c7; border: 1px solid #f59e0b; border-radius: 8px; font-size: 12px; }
            @media print { body { padding: 20px; } .disclaimer { break-inside: avoid; } }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>${getResultTitle()}</h1>
            <div style="color: #6b7280; font-size: 14px;">Generated: ${new Date().toLocaleDateString()}</div>
          </div>
          <div class="content">${htmlContent}</div>
          <div class="disclaimer">
            <strong>Legal Disclaimer:</strong> This document is for informational purposes only and does not constitute legal advice. Consider consulting with an attorney before signing any legal agreement.
          </div>
        </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    printWindow.close();
  };

  const exportResult = async (format: 'word' | 'text' | 'pdf') => {
    const result = getCurrentResult();
    if (!result) return;

    try {
      const extensionMap: Record<string, string> = { word: 'docx', pdf: 'pdf', text: 'txt' };
      const extension = extensionMap[format];
      const filename = `contract-negotiation-${selectedWorkflow}-${new Date().toISOString().split('T')[0]}.${extension}`;

      const headerText = `${getResultTitle()}\nGenerated: ${new Date().toLocaleDateString()}\n\n`;
      const footerText = `\n\n---\nLegal Disclaimer: This document is for informational purposes only and does not constitute legal advice.`;
      const fullContent = headerText + result + footerText;

      const blob = await exportAPI.exportDocument({
        content: fullContent,
        title: getResultTitle(),
        format: extension as 'docx' | 'pdf' | 'txt'
      });

      if ('showSaveFilePicker' in window) {
        try {
          const mimeTypes: Record<string, string> = {
            docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
            pdf: 'application/pdf',
            txt: 'text/plain'
          };

          const descriptions: Record<string, string> = {
            word: 'Word Document',
            pdf: 'PDF Document',
            text: 'Text File'
          };

          const handle = await (window as any).showSaveFilePicker({
            suggestedName: filename,
            types: [{
              description: descriptions[format],
              accept: { [mimeTypes[extension]]: [`.${extension}`] }
            }]
          });

          const writable = await handle.createWritable();
          await writable.write(blob);
          await writable.close();
          return;
        } catch (err: any) {
          if (err.name === 'AbortError') return;
        }
      }

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Export error:', error);
      setError(t('contractNegotiation:errors.exportFailed', 'Failed to export document'));
    }
  };

  // Navigation
  const getCurrentSteps = () => {
    switch (selectedWorkflow) {
      case 'analyze': return ANALYZE_STEPS;
      case 'strategy': return STRATEGY_STEPS;
      case 'redline': return REDLINE_STEPS;
      case 'talking-points': return TALKING_POINTS_STEPS;
      default: return [];
    }
  };

  const canProceed = () => {
    const steps = getCurrentSteps();
    const currentStepName = steps[currentStep];

    switch (currentStepName) {
      case 'uploadContract':
        return contractFile || contractText.trim().length > 0;
      case 'priorities':
        return selectedPriorities.length > 0;
      default:
        return true;
    }
  };

  const handleNext = async () => {
    const steps = getCurrentSteps();
    const currentStepName = steps[currentStep];
    const nextStepName = steps[currentStep + 1];

    // Trigger appropriate API call when moving to results step
    if (selectedWorkflow === 'analyze' && nextStepName === 'analysis') {
      await analyzeContract();
    } else if (selectedWorkflow === 'strategy' && nextStepName === 'strategy') {
      await generateStrategy();
    } else if (selectedWorkflow === 'redline' && nextStepName === 'redline') {
      await generateRedline();
    } else if (selectedWorkflow === 'talking-points' && nextStepName === 'talkingPoints') {
      await generateTalkingPoints();
    }

    setCurrentStep(Math.min(steps.length - 1, currentStep + 1));
  };

  const handleBack = () => {
    if (currentStep === 0) {
      setSelectedWorkflow(null);
      resetState();
    } else {
      setCurrentStep(Math.max(0, currentStep - 1));
    }
  };

  const resetState = () => {
    setCurrentStep(0);
    setContractFile(null);
    setContractText('');
    setSelectedContractType('');
    setIndustry('');
    setSelectedPriorities([]);
    setYourPosition('');
    setSpecificConcerns('');
    setNegotiationContext('');
    setAnalysisResult(null);
    setStrategyResult(null);
    setRedlineResult(null);
    setTalkingPointsResult(null);
    setError(null);
  };

  const selectWorkflow = (workflow: WorkflowType) => {
    resetState();
    setSelectedWorkflow(workflow);
  };

  // Render workflow selection
  const renderWorkflowSelection = () => (
    <div className="space-y-8">
      <div className="text-center">
        <p className="text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
          {t('contractNegotiation:workflowSelection.description', 'Choose how you want to work with your contract. Upload a contract and get AI-powered analysis, negotiation strategies, redlines, or talking points.')}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Analyze Contract */}
        <button
          onClick={() => selectWorkflow('analyze')}
          className="group p-6 bg-white dark:bg-gray-800 rounded-xl border-2 border-blue-200 dark:border-blue-800 hover:border-blue-500 dark:hover:border-blue-500 hover:shadow-lg transition-all text-left"
        >
          <div className="flex items-center mb-4">
            <div className="p-3 bg-blue-100 dark:bg-blue-900/50 rounded-lg group-hover:bg-blue-200 dark:group-hover:bg-blue-900 transition-colors">
              <DocumentMagnifyingGlassIcon className="h-8 w-8 text-blue-600 dark:text-blue-400" />
            </div>
          </div>
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
            {t('contractNegotiation:workflows.analyze.title', 'Analyze Contract')}
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            {t('contractNegotiation:workflows.analyze.description', 'Identify negotiable terms, assess risks, and compare to industry standards.')}
          </p>
          <div className="flex items-center text-blue-600 dark:text-blue-400 font-medium">
            {t('contractNegotiation:workflowSelection.getStarted', 'Get Started')}
            <ArrowRightIcon className="h-5 w-5 ml-2 group-hover:translate-x-1 transition-transform" />
          </div>
        </button>

        {/* Negotiation Strategy */}
        <button
          onClick={() => selectWorkflow('strategy')}
          className="group p-6 bg-white dark:bg-gray-800 rounded-xl border-2 border-green-200 dark:border-green-800 hover:border-green-500 dark:hover:border-green-500 hover:shadow-lg transition-all text-left"
        >
          <div className="flex items-center mb-4">
            <div className="p-3 bg-green-100 dark:bg-green-900/50 rounded-lg group-hover:bg-green-200 dark:group-hover:bg-green-900 transition-colors">
              <ScaleIcon className="h-8 w-8 text-green-600 dark:text-green-400" />
            </div>
          </div>
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
            {t('contractNegotiation:workflows.strategy.title', 'Negotiation Strategy')}
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            {t('contractNegotiation:workflows.strategy.description', 'Get counter-proposals, alternative language, and predicted outcomes.')}
          </p>
          <div className="flex items-center text-green-600 dark:text-green-400 font-medium">
            {t('contractNegotiation:workflowSelection.getStarted', 'Get Started')}
            <ArrowRightIcon className="h-5 w-5 ml-2 group-hover:translate-x-1 transition-transform" />
          </div>
        </button>

        {/* Redlining */}
        <button
          onClick={() => selectWorkflow('redline')}
          className="group p-6 bg-white dark:bg-gray-800 rounded-xl border-2 border-purple-200 dark:border-purple-800 hover:border-purple-500 dark:hover:border-purple-500 hover:shadow-lg transition-all text-left"
        >
          <div className="flex items-center mb-4">
            <div className="p-3 bg-purple-100 dark:bg-purple-900/50 rounded-lg group-hover:bg-purple-200 dark:group-hover:bg-purple-900 transition-colors">
              <PencilSquareIcon className="h-8 w-8 text-purple-600 dark:text-purple-400" />
            </div>
          </div>
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
            {t('contractNegotiation:workflows.redline.title', 'Generate Redlines')}
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            {t('contractNegotiation:workflows.redline.description', 'Get a redlined version with proposed changes and comments.')}
          </p>
          <div className="flex items-center text-purple-600 dark:text-purple-400 font-medium">
            {t('contractNegotiation:workflowSelection.getStarted', 'Get Started')}
            <ArrowRightIcon className="h-5 w-5 ml-2 group-hover:translate-x-1 transition-transform" />
          </div>
        </button>

        {/* Talking Points */}
        <button
          onClick={() => selectWorkflow('talking-points')}
          className="group p-6 bg-white dark:bg-gray-800 rounded-xl border-2 border-orange-200 dark:border-orange-800 hover:border-orange-500 dark:hover:border-orange-500 hover:shadow-lg transition-all text-left"
        >
          <div className="flex items-center mb-4">
            <div className="p-3 bg-orange-100 dark:bg-orange-900/50 rounded-lg group-hover:bg-orange-200 dark:group-hover:bg-orange-900 transition-colors">
              <ChatBubbleLeftRightIcon className="h-8 w-8 text-orange-600 dark:text-orange-400" />
            </div>
          </div>
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
            {t('contractNegotiation:workflows.talkingPoints.title', 'Talking Points')}
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            {t('contractNegotiation:workflows.talkingPoints.description', 'Generate a discussion guide with objection responses and leverage points.')}
          </p>
          <div className="flex items-center text-orange-600 dark:text-orange-400 font-medium">
            {t('contractNegotiation:workflowSelection.getStarted', 'Get Started')}
            <ArrowRightIcon className="h-5 w-5 ml-2 group-hover:translate-x-1 transition-transform" />
          </div>
        </button>
      </div>
    </div>
  );

  // Render upload contract step
  const renderUploadContract = () => (
    <div className="space-y-6">
      <p className="text-gray-600 dark:text-gray-400">
        {t('contractNegotiation:upload.description', 'Upload or paste the contract you want to work with.')}
      </p>

      {/* Contract Type Selection */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          {t('contractNegotiation:upload.contractType', 'Contract Type')}
          <span className="text-gray-400 ml-1">({t('common:optional', 'optional')})</span>
        </label>
        <select
          value={selectedContractType}
          onChange={(e) => setSelectedContractType(e.target.value)}
          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
        >
          <option value="">{t('contractNegotiation:upload.selectType', 'Select contract type...')}</option>
          {contractTypes.map((type) => (
            <option key={type.id} value={type.id}>
              {type.name}
            </option>
          ))}
        </select>
      </div>

      {/* Upload Document */}
      <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
        <h4 className="font-medium text-blue-900 dark:text-blue-100 mb-3 flex items-center">
          <ArrowUpTrayIcon className="h-5 w-5 mr-2" />
          {t('contractNegotiation:upload.uploadTitle', 'Option 1: Upload Document')}
        </h4>
        <p className="text-sm text-blue-700 dark:text-blue-300 mb-3">
          {t('contractNegotiation:upload.uploadHint', 'Upload a PDF, Word, or text document.')}
        </p>
        <div className="flex flex-wrap items-center gap-3">
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.doc,.docx,.txt"
            onChange={handleFileSelect}
            className="hidden"
            id="contract-file-input"
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={isLoading}
            className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-700 border border-blue-300 dark:border-blue-600 rounded-lg hover:bg-blue-50 dark:hover:bg-gray-600 transition-colors"
          >
            <ArrowUpTrayIcon className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            <span className="text-blue-700 dark:text-blue-300">
              {t('contractNegotiation:upload.selectDocument', 'Select Document')}
            </span>
          </button>
          {contractFile && (
            <div className="flex items-center gap-2 px-3 py-2 bg-white dark:bg-gray-700 rounded-lg border border-blue-200 dark:border-blue-700">
              <DocumentTextIcon className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              <span className="text-sm text-blue-800 dark:text-blue-200 max-w-[200px] truncate">
                {contractFile.name}
              </span>
              <button onClick={clearFile} className="p-1 hover:bg-blue-100 dark:hover:bg-blue-800 rounded">
                <XMarkIcon className="h-4 w-4 text-blue-600 dark:text-blue-400" />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Divider */}
      <div className="relative">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-gray-300 dark:border-gray-600"></div>
        </div>
        <div className="relative flex justify-center text-sm">
          <span className="px-4 bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400">
            {t('contractNegotiation:upload.or', 'OR')}
          </span>
        </div>
      </div>

      {/* Paste Text */}
      <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
        <h4 className="font-medium text-green-900 dark:text-green-100 mb-3 flex items-center">
          <ClipboardDocumentListIcon className="h-5 w-5 mr-2" />
          {t('contractNegotiation:upload.pasteTitle', 'Option 2: Paste Contract Text')}
        </h4>
        <textarea
          value={contractText}
          onChange={(e) => {
            setContractText(e.target.value);
            if (contractFile) clearFile();
          }}
          rows={10}
          placeholder={t('contractNegotiation:upload.pastePlaceholder', 'Paste your contract text here...')}
          className="w-full px-4 py-3 border border-green-300 dark:border-green-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-mono text-sm"
        />
      </div>

      {/* Status */}
      {(contractFile || contractText.trim()) && (
        <div className="p-4 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded-lg">
          <div className="flex items-center">
            <CheckCircleIcon className="h-5 w-5 text-green-600 dark:text-green-400 mr-2" />
            <span className="text-green-800 dark:text-green-200">
              {t('contractNegotiation:upload.ready', 'Contract ready. Click Next to continue.')}
            </span>
          </div>
        </div>
      )}
    </div>
  );

  // Render priorities step
  const renderPriorities = () => (
    <div className="space-y-6">
      <p className="text-gray-600 dark:text-gray-400">
        {t('contractNegotiation:priorities.description', 'Select your negotiation priorities. This helps us tailor the strategy to your needs.')}
      </p>

      {/* Priority Selection */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
          {t('contractNegotiation:priorities.selectPriorities', 'Select Your Priorities')} *
        </label>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {priorities.map((priority) => (
            <button
              key={priority.id}
              onClick={() => togglePriority(priority.id)}
              className={`p-4 rounded-lg border-2 text-left transition-all ${
                selectedPriorities.includes(priority.id)
                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30'
                  : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
              }`}
            >
              <div className="flex items-center justify-between">
                <span className={`font-medium ${
                  selectedPriorities.includes(priority.id)
                    ? 'text-blue-900 dark:text-blue-100'
                    : 'text-gray-900 dark:text-white'
                }`}>
                  {priority.name}
                </span>
                {selectedPriorities.includes(priority.id) && (
                  <CheckCircleIcon className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                )}
              </div>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                {priority.description}
              </p>
            </button>
          ))}
        </div>
      </div>

      {/* Your Position */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          {t('contractNegotiation:priorities.yourPosition', 'Your Position')}
          <span className="text-gray-400 ml-1">({t('common:optional', 'optional')})</span>
        </label>
        <input
          type="text"
          value={yourPosition}
          onChange={(e) => setYourPosition(e.target.value)}
          placeholder={t('contractNegotiation:priorities.positionPlaceholder', 'e.g., buyer, employee, tenant, contractor...')}
          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
        />
      </div>

      {/* Specific Concerns / Context */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          {selectedWorkflow === 'talking-points'
            ? t('contractNegotiation:priorities.negotiationContext', 'Negotiation Context')
            : t('contractNegotiation:priorities.specificConcerns', 'Specific Concerns or Changes')}
          <span className="text-gray-400 ml-1">({t('common:optional', 'optional')})</span>
        </label>
        {selectedWorkflow === 'talking-points' ? (
          <select
            value={negotiationContext}
            onChange={(e) => setNegotiationContext(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          >
            <option value="">{t('contractNegotiation:priorities.selectContext', 'Select context...')}</option>
            <option value="phone">{t('contractNegotiation:priorities.contextPhone', 'Phone Call')}</option>
            <option value="in-person">{t('contractNegotiation:priorities.contextInPerson', 'In-Person Meeting')}</option>
            <option value="video">{t('contractNegotiation:priorities.contextVideo', 'Video Call')}</option>
            <option value="email">{t('contractNegotiation:priorities.contextEmail', 'Email Exchange')}</option>
          </select>
        ) : (
          <textarea
            value={specificConcerns}
            onChange={(e) => setSpecificConcerns(e.target.value)}
            rows={4}
            placeholder={t('contractNegotiation:priorities.concernsPlaceholder', 'Describe any specific concerns or changes you want to focus on...')}
            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          />
        )}
      </div>

      {selectedPriorities.length > 0 && (
        <div className="p-4 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded-lg">
          <div className="flex items-center">
            <CheckCircleIcon className="h-5 w-5 text-green-600 dark:text-green-400 mr-2" />
            <span className="text-green-800 dark:text-green-200">
              {t('contractNegotiation:priorities.selected', '{{count}} priorities selected', { count: selectedPriorities.length })}
            </span>
          </div>
        </div>
      )}
    </div>
  );

  // Render results
  const renderResults = () => {
    const result = getCurrentResult();

    return (
      <div className="space-y-6">
        {isLoading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent"></div>
            <p className="mt-4 text-gray-600 dark:text-gray-400">
              {t('contractNegotiation:results.processing', 'Processing your contract...')}
            </p>
          </div>
        ) : result ? (
          <>
            <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
              <div className="flex justify-between items-start">
                <div>
                  <h4 className="font-medium text-blue-900 dark:text-blue-100 mb-2 flex items-center">
                    <CheckCircleIcon className="h-5 w-5 mr-2" />
                    {getResultTitle()}
                  </h4>
                </div>
                <ExportDropdownMenu
                  onExport={handleExport}
                  onPrint={printResult}
                  showPrint={true}
                />
              </div>
            </div>

            <div className="p-6 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg">
              <div className="result-content space-y-6">
                <style>{`
                  .result-content h2 {
                    font-size: 1.25rem;
                    font-weight: 700;
                    color: #1e40af;
                    border-bottom: 2px solid #e5e7eb;
                    padding-bottom: 0.5rem;
                    margin-top: 2rem;
                    margin-bottom: 1rem;
                  }
                  .dark .result-content h2 {
                    color: #60a5fa;
                    border-bottom-color: #374151;
                  }
                  .result-content h2:first-child { margin-top: 0; }
                  .result-content h3 {
                    font-size: 1rem;
                    font-weight: 600;
                    color: #1f2937;
                    margin-top: 1rem;
                    margin-bottom: 0.5rem;
                  }
                  .dark .result-content h3 { color: #e5e7eb; }
                  .result-content p {
                    color: #374151;
                    margin: 0.5rem 0;
                    line-height: 1.6;
                  }
                  .dark .result-content p { color: #d1d5db; }
                  .result-content ul, .result-content ol {
                    margin: 0.5rem 0;
                    padding-left: 1.5rem;
                  }
                  .result-content li {
                    color: #374151;
                    margin: 0.25rem 0;
                    line-height: 1.5;
                  }
                  .dark .result-content li { color: #d1d5db; }
                  .result-content strong {
                    color: #111827;
                    font-weight: 600;
                  }
                  .dark .result-content strong { color: #f3f4f6; }
                  .result-content del {
                    background-color: #fecaca;
                    color: #991b1b;
                    text-decoration: line-through;
                  }
                  .dark .result-content del {
                    background-color: #7f1d1d;
                    color: #fecaca;
                  }
                  .result-content blockquote {
                    border-left: 4px solid #3b82f6;
                    padding-left: 1rem;
                    margin: 1rem 0;
                    color: #4b5563;
                    font-style: italic;
                  }
                  .dark .result-content blockquote {
                    border-left-color: #60a5fa;
                    color: #9ca3af;
                  }
                `}</style>
                <ReactMarkdown>{result}</ReactMarkdown>
              </div>
            </div>

            <div className="flex gap-4">
              <button
                onClick={() => {
                  setCurrentStep(0);
                  setAnalysisResult(null);
                  setStrategyResult(null);
                  setRedlineResult(null);
                  setTalkingPointsResult(null);
                }}
                className="px-4 py-2 bg-blue-600 dark:bg-blue-700 hover:bg-blue-700 dark:hover:bg-blue-600 text-white rounded-lg transition-colors"
              >
                {t('contractNegotiation:results.startOver', 'Analyze Another Contract')}
              </button>
            </div>
          </>
        ) : (
          <div className="text-center py-12 text-gray-500 dark:text-gray-400">
            {t('contractNegotiation:results.noResults', 'No results yet. Go back and upload a contract.')}
          </div>
        )}
      </div>
    );
  };

  // Render step content
  const renderStepContent = () => {
    const steps = getCurrentSteps();
    const currentStepName = steps[currentStep];

    switch (currentStepName) {
      case 'uploadContract':
        return renderUploadContract();
      case 'priorities':
        return renderPriorities();
      case 'analysis':
      case 'strategy':
      case 'redline':
      case 'talkingPoints':
        return renderResults();
      default:
        return null;
    }
  };

  const getStepTitle = () => {
    const steps = getCurrentSteps();
    const currentStepName = steps[currentStep];

    const titles: Record<string, string> = {
      uploadContract: t('contractNegotiation:steps.upload', 'Upload Contract'),
      priorities: t('contractNegotiation:steps.priorities', 'Set Priorities'),
      analysis: t('contractNegotiation:steps.analysis', 'Analysis Results'),
      strategy: t('contractNegotiation:steps.strategy', 'Negotiation Strategy'),
      redline: t('contractNegotiation:steps.redline', 'Redlined Contract'),
      talkingPoints: t('contractNegotiation:steps.talkingPoints', 'Talking Points'),
    };

    return titles[currentStepName] || currentStepName;
  };

  const getStepIcon = (stepName: string) => {
    const icons: Record<string, React.ElementType> = {
      uploadContract: ArrowUpTrayIcon,
      priorities: ScaleIcon,
      analysis: DocumentMagnifyingGlassIcon,
      strategy: ScaleIcon,
      redline: PencilSquareIcon,
      talkingPoints: ChatBubbleLeftRightIcon,
    };
    return icons[stepName] || DocumentTextIcon;
  };

  const getWorkflowColor = () => {
    switch (selectedWorkflow) {
      case 'analyze': return 'blue';
      case 'strategy': return 'green';
      case 'redline': return 'purple';
      case 'talking-points': return 'orange';
      default: return 'blue';
    }
  };

  // Main render
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
      {/* Loading Overlay for contract processing */}
      <LoadingOverlay
        isVisible={isLoading}
        message={t('contractNegotiation:loading.processing', 'Processing...')}
        subMessage={t('contractNegotiation:loading.pleaseWait', 'Analyzing your contract')}
      />

      <div className="max-w-4xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-4">
            <DocumentDuplicateIcon className="h-12 w-12 text-blue-600 dark:text-blue-400" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
            {t('contractNegotiation:title', 'Contract Negotiation Assistant')}
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            {t('contractNegotiation:subtitle', 'AI-powered contract analysis and negotiation support')}
          </p>
        </div>

        {/* Step Progress */}
        {selectedWorkflow && (
          <div className="mb-8">
            <div className="flex items-center justify-between">
              {getCurrentSteps().map((step, index) => {
                const Icon = getStepIcon(step);
                const isActive = index === currentStep;
                const isCompleted = index < currentStep;
                const color = getWorkflowColor();

                return (
                  <React.Fragment key={step}>
                    <div className="flex flex-col items-center">
                      <button
                        onClick={() => index < currentStep && setCurrentStep(index)}
                        disabled={index > currentStep}
                        className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
                          isActive
                            ? `bg-${color}-600 dark:bg-${color}-500 text-white`
                            : isCompleted
                            ? `bg-${color}-500 text-white cursor-pointer`
                            : 'bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                        } ${index < currentStep ? 'cursor-pointer' : index > currentStep ? 'cursor-not-allowed' : ''}`}
                      >
                        {isCompleted ? (
                          <CheckCircleIcon className="h-6 w-6" />
                        ) : (
                          <Icon className="h-5 w-5" />
                        )}
                      </button>
                      <span
                        className={`text-xs mt-2 hidden sm:block text-center max-w-[80px] ${
                          isActive
                            ? `text-${color}-600 dark:text-${color}-400 font-medium`
                            : 'text-gray-500 dark:text-gray-400'
                        }`}
                      >
                        {t(`contractNegotiation:stepLabels.${step}`, step.replace(/([A-Z])/g, ' $1').trim())}
                      </span>
                    </div>
                    {index < getCurrentSteps().length - 1 && (
                      <div
                        className={`flex-1 h-1 mx-2 rounded ${
                          index < currentStep ? `bg-${color}-500` : 'bg-gray-200 dark:bg-gray-700'
                        }`}
                      />
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        )}

        {/* Main Content */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 mb-6">
          {selectedWorkflow ? (
            <>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">
                {getStepTitle()}
              </h2>

              {error && (
                <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg">
                  <p className="text-red-700 dark:text-red-300">{error}</p>
                </div>
              )}

              {renderStepContent()}
            </>
          ) : (
            renderWorkflowSelection()
          )}
        </div>

        {/* Navigation */}
        {selectedWorkflow && (
          <div className="flex justify-between">
            <button
              onClick={handleBack}
              className="flex items-center px-6 py-3 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-800 dark:text-white rounded-lg font-medium transition-colors"
            >
              <ArrowLeftIcon className="h-5 w-5 mr-2" />
              {currentStep === 0
                ? t('contractNegotiation:navigation.changeWorkflow', 'Change Workflow')
                : t('contractNegotiation:navigation.back', 'Back')}
            </button>

            {currentStep < getCurrentSteps().length - 1 && (
              <button
                onClick={handleNext}
                disabled={!canProceed() || isLoading}
                className={`flex items-center px-6 py-3 bg-${getWorkflowColor()}-600 dark:bg-${getWorkflowColor()}-700 hover:bg-${getWorkflowColor()}-700 dark:hover:bg-${getWorkflowColor()}-600 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                {isLoading ? (
                  <>
                    <svg className="animate-spin h-5 w-5 mr-2" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    {t('contractNegotiation:navigation.processing', 'Processing...')}
                  </>
                ) : (
                  <>
                    {t('contractNegotiation:navigation.next', 'Next')}
                    <ArrowRightIcon className="h-5 w-5 ml-2" />
                  </>
                )}
              </button>
            )}
          </div>
        )}

        {/* Disclaimer */}
        <div className="mt-8 p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
          <div className="flex items-start">
            <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600 dark:text-yellow-400 mt-0.5 mr-3 flex-shrink-0" />
            <div>
              <h4 className="font-medium text-yellow-800 dark:text-yellow-200">
                {t('contractNegotiation:disclaimer.title', 'Legal Disclaimer')}
              </h4>
              <p className="text-sm text-yellow-700 dark:text-yellow-300 mt-1">
                {t('contractNegotiation:disclaimer.text', 'This tool provides general information about contract negotiation and generates documents for educational purposes only. It does not constitute legal advice. Contract terms vary significantly by jurisdiction and circumstance. Always verify information and consider consulting with a licensed attorney before signing any legal agreement.')}
              </p>
            </div>
          </div>
        </div>
      </div>

    </div>
  );
};
